import { jwtDecode, type JwtPayload } from "jwt-decode";

import {
    type FundDataWithType,
    type ShareClassDataWithType,
} from "@hsl/fund-explorer/metaSchemas";
import {
    type SubscribableAsset,
    type SubscribableAssetCreate,
    type Subscriptions,
} from "@hsl/lgim-explorer/src/openapi";

import {
    type NotificationQItem,
    type NotificationType,
    type User,
} from "./types";

export function getShareClassId(sc: ShareClassDataWithType) {
    return sc.type === "pmc"
        ? sc.data.data.scopeCode
        : sc.type === "etf"
          ? sc.data.data.shareclassISIN
          : sc.data.data.isin;
}

export function getSubscriptionEntityId(subId: string) {
    const [, entityId] = subId.split("_");
    return entityId;
}

export function getSubscriptionSetCode(subId: string) {
    const [, , setCode] = subId.split("_");
    return setCode;
}

export function getUserInviteIds() {
    const urlParams = new URLSearchParams(window.location.search);
    const userId = urlParams.get("userId");
    const inviteId = urlParams.get("inviteId");
    const salesforceToken = urlParams.get("token");

    const url = new URL(window.location.href);
    url.searchParams.delete("userId");
    url.searchParams.delete("inviteId");
    url.searchParams.delete("token");

    window.history.replaceState({}, "", url.toString());
    return { userId, inviteId, salesforceToken };
}

export function decodeToken(token: string | null) {
    try {
        if (!token) return null;
        const decoded = jwtDecode(token);
        if (!decoded) throw new Error("Invalid token");
        if (typeof decoded === "string") throw new Error("Invalid token");
        const { exp, sub } = decoded;
        if (!exp) throw new Error("Invalid token - no expiry");
        if (!sub) throw new Error("Invalid token - no subject");
        // exp is in seconds, Date.now() is in ms
        if (exp * 1000 < Date.now()) throw new Error("Token expired");
        return { exp, sub } as Required<JwtPayload>;
    } catch (err) {
        console.error(err);
        return null;
    }
}

export function createNotificationQItem(
    type: NotificationType,
): NotificationQItem {
    //using timestamps for now
    return { type, id: `${Date.now()}` };
}

export function createFundWatchData(
    fund: FundDataWithType,
): SubscribableAssetCreate {
    return {
        entityId: getSubscriptionEntityId(fund.data.data.watchlistID ?? ""),
        fundId: `${fund.type}-${fund.data.id}`,
        subscriptionId: fund.data.data.watchlistID,
        subscriptionType: "WATCHLIST",
        subscriptionLevel: "FUND",
        literatureSetCode: "",
        frequency: "DAILY",
    };
}

export function getUserType(
    userId?: string | null,
    inviteId?: string | null,
    storedToken?: string | null,
): User["userType"] {
    if (storedToken) {
        return "existing";
    }
    if (!userId || !inviteId || window.location.pathname.includes("my-funds")) {
        return "new";
    }
    return "returning";
}

export function getWelcomeNotificationType(
    userType: "returning" | "new",
    subscriptions: Subscriptions,
): NotificationType | null {
    const actionType =
        subscriptions &&
        subscriptions.length > 0 &&
        subscriptions[0]?.subscriptionType;
    if (!actionType) {
        return null;
    }
    switch (actionType) {
        case "FULL":
            return userType === "new" ? "WMF" : "WB";
        case "WATCHLIST":
            return userType === "new" ? "WMF_WATCH" : "WB_WATCH";
    }
    return null;
}

export function newSubscriptions(
    existing: Subscriptions,
    candidates: Subscriptions,
) {
    return candidates.filter((c) =>
        existing.every((e) => e.subscriptionId !== c.subscriptionId),
    );
}

export function getSubAssetFundId(sub: SubscribableAsset) {
    return sub.fundId?.split("-")[1];
}
